import {
  isPortalFamilyHostLabel,
  isReservedPortalLeafSlug,
  portalHostLabel as sharedPortalHostLabel,
} from "@l30/shared";

const TENANT_PATH = /^\/t\/([a-z0-9-]+)(?:\/|$)/i;
const CONFIGURED_PORTAL_ROOT = import.meta.env.VITE_PORTAL_DOMAIN_ROOT;

/**
 * Leaf tenant slug from SPA pathname (`/t/testra/portal` → `testra`).
 */
function tenantSlugFromPathname(pathname: string): string | null {
  const m = pathname.match(TENANT_PATH);
  if (!m) return null;
  const slug = m[1]!.toLowerCase();
  if (isReservedPortalLeafSlug(slug)) return null;
  return slug;
}

/**
 * Host leaf label (`testra.portal…` → `testra`, `devices.portal…` → `devices`).
 */
export function portalHostLabel(host: string): string | null {
  const additionalRoots =
    typeof CONFIGURED_PORTAL_ROOT === "string" && CONFIGURED_PORTAL_ROOT.length > 0
      ? [CONFIGURED_PORTAL_ROOT]
      : [];
  return sharedPortalHostLabel(host, additionalRoots);
}

/**
 * Resolve the active leaf tenant for brand + API header:
 *   1. `/t/<slug>/…` path (family hosts + local path routing)
 *   2. Leaf host label (legacy flat `testra.portal…`)
 *   3. Family host `gop` without path → tenant `gop` (dual-mode)
 */
export function resolvePortalTenantSlug(args?: {
  pathname?: string;
  host?: string;
}): string | null {
  const pathname =
    args?.pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  const host = args?.host ?? (typeof window !== "undefined" ? window.location.host : "");

  const fromPath = tenantSlugFromPathname(pathname);
  if (fromPath) return fromPath;

  const label = portalHostLabel(host);
  if (!label) return null;
  if (label === "devices") return null;
  if (isPortalFamilyHostLabel(label)) {
    return label === "gop" ? "gop" : null;
  }
  return label;
}

/** Prefix a portal-relative path with `/t/<slug>`. */
export function tenantScopedPath(tenantSlug: string, path: string): string {
  const normalised = path.startsWith("/") ? path : `/${path}`;
  if (normalised.startsWith(`/t/${tenantSlug}/`) || normalised === `/t/${tenantSlug}`) {
    return normalised;
  }
  return `/t/${tenantSlug.toLowerCase()}${normalised}`;
}

/** Sign-in path for the current (or given) tenant. */
export function portalSignInPath(tenantSlug?: string | null): string {
  const slug = tenantSlug ?? resolvePortalTenantSlug();
  if (!slug) return "/portal/sign-in";
  return tenantScopedPath(slug, "/portal/sign-in");
}
